import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';

// Environment variables
const {
    REACT_APP_SENTRY_DSN,
    REACT_APP_ENVIRONMENT,
    REACT_APP_VERSION,
    NODE_ENV
} = process.env;

const appVersion =
    typeof REACT_APP_VERSION === 'string' && REACT_APP_VERSION.trim().length > 0
        ? REACT_APP_VERSION.trim()
        : undefined;

// Create history instance that will be shared
export const history = createBrowserHistory();

// Only initialize Sentry if DSN is provided
if (REACT_APP_SENTRY_DSN) {
    Sentry.init({
        dsn: REACT_APP_SENTRY_DSN,

        // Environment configuration
        environment: REACT_APP_ENVIRONMENT || NODE_ENV || 'development',
        release: appVersion ? `orchard-hand-web@${appVersion}` : undefined,

        // Integrations
        integrations: [
            // Performance Monitoring with React Router v5
            Sentry.reactRouterV5BrowserTracingIntegration({
                history,
                // Trace API calls
                tracePropagationTargets: [
                    /^\/api/,
                    /^https:\/\/app-development\.orchardhand\.com\/api/,
                    /^https:\/\/app-staging\.orchardhand\.com\/api/,
                    /^https:\/\/app\.orchardhand\.com\/api/,
                    /^https:\/\/users-develop\.tandg\.digital/,
                    /^https:\/\/users-staging\.tandg\.digital/,
                    /^https:\/\/users\.tandg\.digital/
                ],
                // Exclude third-party services from traces
                // This is the official Sentry approach per:
                // https://docs.sentry.io/platforms/javascript/tracing/instrumentation/automatic-instrumentation/#shouldcreatespanforrequest
                shouldCreateSpanForRequest: (url) => {
                    // Don't create spans for Rapid7/LogEntries
                    if (url.includes('logs.insight.rapid7.com')) {
                        return false;
                    }
                    if (
                        url.includes('r7insight') ||
                        url.includes('logentries')
                    ) {
                        return false;
                    }

                    // Don't create spans for Google Analytics
                    if (url.includes('google-analytics.com')) {
                        return false;
                    }
                    if (url.includes('/g/collect')) {
                        return false;
                    }
                    if (url.includes('/j/collect')) {
                        return false;
                    }
                    if (url.includes('/r/collect')) {
                        return false;
                    }

                    // Don't create spans for Google Tag Manager
                    if (url.includes('googletagmanager.com')) {
                        return false;
                    }
                    if (url.includes('/gtm.js')) {
                        return false;
                    }

                    // Create spans for everything else
                    return true;
                }
            })
            // Session Replay removed for privacy compliance
        ],

        // Performance Monitoring Sample Rates
        tracesSampleRate: NODE_ENV === 'production' ? 0.1 : 1.0,

        // Session Replay - PERMANENTLY DISABLED
        // DO NOT ADD: replayIntegration() or replayCanvasIntegration()
        // Session Replay is disabled for privacy compliance and should not be enabled.
        // This decision is intentional and permanent.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,

        // Privacy & Security
        sendDefaultPii: true,

        // Filtering & Sampling
        beforeSend(event, hint) {
            // Filter out browser extension errors
            if (event.exception) {
                const frames = event.exception.values?.[0]?.stacktrace?.frames;
                if (
                    frames &&
                    frames.some(
                        (frame) =>
                            frame.filename?.includes('chrome-extension://') ||
                            frame.filename?.includes('moz-extension://') ||
                            frame.filename?.includes('safari-extension://') ||
                            frame.filename?.includes('r7insight') ||
                            frame.filename?.includes('logentries')
                    )
                ) {
                    return null;
                }
            }

            // Filter out LogEntries/Rapid7 related errors
            const errorMessage = event.exception?.values?.[0]?.value || '';
            const errorType = event.exception?.values?.[0]?.type || '';

            if (
                errorMessage.includes('R7Insight') ||
                errorMessage.includes('LogEntries') ||
                errorMessage.includes('le_js') ||
                errorType.includes('R7Insight') ||
                event.tags?.component === 'logentries'
            ) {
                return null;
            }

            // Filter out spans from third-party analytics services
            const filteredSpans = event.spans?.filter((span) => {
                const description = span.description || '';
                const op = span.op || '';

                // Remove HTTP spans to analytics services
                if (op.startsWith('http')) {
                    if (
                        description.includes('google-analytics.com') ||
                        description.includes('googletagmanager.com') ||
                        description.includes('logs.insight.rapid7.com') ||
                        description.includes('r7insight') ||
                        description.includes('logentries') ||
                        description.includes('/g/collect') ||
                        description.includes('/gtm.js')
                    ) {
                        return false;
                    }
                }

                return true;
            });

            // Adjust level for network errors
            const originalException = hint.originalException as
                | {
                      message?: unknown;
                  }
                | undefined;

            const { message } = (originalException ?? {}) as {
                message?: unknown;
            };

            if (typeof message === 'string') {
                if (
                    message.includes('Network request failed') ||
                    message.includes('Load failed') ||
                    message.includes('cancelled')
                ) {
                    return {
                        ...event,
                        spans: filteredSpans,
                        level: 'warning' as Sentry.SeverityLevel
                    };
                }
            }

            return {
                ...event,
                spans: filteredSpans
            };
        },

        // Ignore common noise
        ignoreErrors: [
            'top.GLOBALS',
            'chrome-extension://',
            'moz-extension://',
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',
            'Auth0 login required',
            // Ignore LogEntries/Rapid7 related errors
            'R7Insight',
            'LogEntries',
            'le_js',
            'Rapid7'
        ],

        denyUrls: [
            /extensions\//i,
            /^chrome:\/\//i,
            /^moz-extension:\/\//i,
            // Block LogEntries/Rapid7 script URLs
            /r7insight/i,
            /logentries/i,
            /logs\.insight\.rapid7\.com/i,
            // Block Google Analytics & GTM URLs
            /google-analytics\.com/i,
            /googletagmanager\.com/i,
            /\/gtm\.js/i,
            /\/g\/collect/i
        ]
    });
}

export default Sentry;
